import { useCallback, useId, useRef } from 'react';
import { Camera, Images } from 'lucide-react';

// The field's front door: a bar pinned to the bottom of the Files tab on a phone,
// where the job is to photograph a site and get on with the work.
//
// Deliberately not built on FileDropZone. That primitive exists for drag-and-drop
// with one input behind it; there is no dragging on a phone, and the two entry
// points here need two differently-configured inputs. Bending it into that shape
// would be reuse of machinery a touch device never runs.

/** Big enough to hit with a thumb in gloves, which is the actual use. */
const TOUCH_TARGET = 'min-h-[44px]';

/**
 * Which button produced the files. It matters downstream: a shot taken through
 * the camera exists only in this page until someone saves it, while a library
 * pick is already in the phone's camera roll.
 */
export type JobFileCaptureSource = 'camera' | 'library';

interface JobFileCaptureBarProps {
  /** The raw selection. Type filtering and toasting belong to the caller. */
  onFiles: (files: File[], source: JobFileCaptureSource) => void;
}

interface CaptureButtonProps {
  id: string;
  label: string;
  icon: React.ReactNode;
  /** Camera-only: iOS opens the capture UI instead of the library. */
  capture?: 'environment';
  multiple?: boolean;
  source: JobFileCaptureSource;
  onFiles: (files: File[], source: JobFileCaptureSource) => void;
}

function CaptureButton({ id, label, icon, capture, multiple, source, onFiles }: CaptureButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      // Resetting first matters more than it looks: without it, picking the same
      // photo twice in a row fires no change event and the second tap does nothing.
      e.target.value = '';
      if (files.length > 0) onFiles(files, source);
    },
    [onFiles, source],
  );

  return (
    <>
      <button
        type="button"
        aria-controls={id}
        onClick={() => inputRef.current?.click()}
        className={`flex flex-1 items-center justify-center gap-2 rounded-md bg-blue-600 px-3 py-2.5 text-sm font-semibold text-white active:bg-blue-700 ${TOUCH_TARGET}`}
      >
        {icon}
        {label}
      </button>
      <input
        id={id}
        ref={inputRef}
        type="file"
        accept="image/*"
        {...(capture ? { capture } : {})}
        multiple={multiple ?? false}
        onChange={handleChange}
        className="hidden"
      />
    </>
  );
}

export function JobFileCaptureBar({ onFiles }: JobFileCaptureBarProps) {
  const prefix = useId();

  return (
    // Fixed rather than sticky: the tab's content is one long scroll, and the bar
    // has to stay put through all of it. Hidden from sm up, where the Upload panel
    // and a real drop target are the better tools.
    <div
      className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white px-3 pb-[env(safe-area-inset-bottom)] pt-2 shadow-[0_-1px_3px_rgba(0,0,0,0.06)] sm:hidden"
      role="group"
      aria-label="Add photos"
    >
      <div className="flex items-stretch gap-2 pb-2">
        <CaptureButton
          id={`${prefix}-camera`}
          label="Take Photo"
          icon={<Camera className="h-5 w-5" />}
          capture="environment"
          source="camera"
          onFiles={onFiles}
        />
        <CaptureButton
          id={`${prefix}-library`}
          label="Choose Photos"
          icon={<Images className="h-5 w-5" />}
          multiple
          source="library"
          onFiles={onFiles}
        />
      </div>
    </div>
  );
}
